import { DeliveryQuote, Fulfillment } from '../../../__generated__/graphql';
import React, { useState } from 'react';
import {
  Button,
  Image,
  Divider,
  FormControl,
  HStack,
  Input,
  Popover,
  Select,
  Text,
  VStack,
  Alert,
} from 'native-base';
import { useMutation } from '@apollo/client';
import { graphql } from '../../../__generated__';
import Loading from '../../../Common/components/Loading';
import {
  iconForShippingMethod,
  nameForShippingMethod,
} from '../../../PatientOrder/utils/shippingMethods';
import currency from 'currency.js';
import { orderQuery } from '../../hooks/useOrder';

type UpdateShippingType = {
  street: string;
  state: string;
  postcode: string;
  suburb: string;
  deliveryMethod: string;
  adjusted: boolean;
  quotes: DeliveryQuote[];
};

export const UpdateShippingModal = ({
  order,
  close,
}: {
  order: Fulfillment;
  close: () => void;
}) => {
  const [newShipping, setNewShipping] = useState<UpdateShippingType>({
    street: order?.street ?? '',
    state: order?.state ?? '',
    postcode: order?.postcode ?? '',
    suburb: order?.suburb ?? '',
    deliveryMethod: order?.deliveryMethod ?? '',
    adjusted: false,
    quotes: [],
  });

  const setValue = (key: keyof UpdateShippingType, value: string) =>
    setNewShipping((existing) => {
      return { ...existing, [key]: value, quotes: [] };
    });

  const [quoteMutation, { called, loading }] = useMutation(
    graphql(`
      mutation shippingQuote(
        $customerID: String
        $suburb: String
        $street: String
        $state: String
        $postcode: String
        $items: [String]
      ) {
        deliveryQuote(
          items: $items
          suburb: $suburb
          state: $state
          postcode: $postcode
          street: $street
          customerID: $customerID
        ) {
          cost
          type
        }
      }
    `),
  );

  const [updateMutation, { loading: updating }] = useMutation(
    graphql(`
      mutation updateOrderShipping(
        $ID: ID
        $suburb: String
        $street: String
        $state: String
        $postcode: String
        $method: String
      ) {
        updateOrderShippingMethod(
          street: $street
          suburb: $suburb
          id: $ID
          state: $state
          deliveryMethod: $method
          postcode: $postcode
        ) {
          shipping
          deliveryMethod
          __typename
          id
          street
          suburb
          state
          postcode
        }
      }
    `),
  );

  const selectedUpdatedMethod = async (method: string) => {
    // @ts-ignore
    if (!confirm(`Confirm updating delivery method to ${nameForShippingMethod(method)}`)) {
      return;
    }

    await updateMutation({
      variables: {
        street: newShipping.street,
        state: newShipping.state,
        suburb: newShipping.suburb,
        postcode: newShipping.postcode,
        ID: order.id,
        method: method,
      },
    });

    close();
  };

  const getDeliveryQuotes = async () => {
    const { data } = await quoteMutation({
      variables: {
        street: newShipping.street,
        state: newShipping.state,
        suburb: newShipping.suburb,
        postcode: newShipping.postcode,
        customerID: order.customer?.id,
        items: (order?.items?.map((a) => a?.ID) ?? []) as string[],
      },
      refetchQueries: [
        {
          query: orderQuery,
          fetchPolicy: 'network-only',
          variables: { ID: order.id },
        },
      ],
    });

    setNewShipping((existing) => {
      return { ...existing, quotes: data?.deliveryQuote as DeliveryQuote[] };
    });
  };

  return (
    <Popover.Content bg={'white'}>
      <Popover.Arrow />
      <Popover.CloseButton />
      <Popover.Header>Change Delivery</Popover.Header>
      <Popover.Body bg={'white'}>
        <VStack space={2}>
          <Alert>The customer is not charged the difference</Alert>
          <FormControl>
            <FormControl.Label>Street</FormControl.Label>

            <Input
              isDisabled={updating}
              value={newShipping.street}
              onChangeText={(e) => setValue('street', e)}
            />
          </FormControl>

          <FormControl flex={1}>
            <FormControl.Label>Suburb</FormControl.Label>

            <Input
              value={newShipping.suburb}
              isDisabled={updating}
              onChangeText={(e) => setValue('suburb', e)}
            />
          </FormControl>

          <HStack space={2}>
            <FormControl flex={1}>
              <FormControl.Label>Postcode</FormControl.Label>

              <Input
                isDisabled={updating}
                value={newShipping.postcode}
                onChangeText={(e) => setValue('postcode', e)}
              />
            </FormControl>

            <FormControl flex={1}>
              <FormControl.Label>State</FormControl.Label>

              <Select
                isDisabled={updating}
                selectedValue={newShipping.state}
                onValueChange={(text) => setValue('state', text)}
              >
                {['ACT', 'NSW', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'NT'].map((state) => (
                  <Select.Item label={state} value={state} />
                ))}
              </Select>
            </FormControl>
          </HStack>

          <Button onPress={getDeliveryQuotes} size={'sm'}>
            Get Delivery Quotes
          </Button>

          <Divider />

          {!loading && !called && newShipping?.quotes?.length === 0 && (
            <Text>Click get delivery quotes to see available delivery options</Text>
          )}

          {!loading && called && newShipping?.quotes?.length === 0 && <Text>No quotes found</Text>}

          {loading && <Loading />}

          {newShipping?.quotes?.map((shippingMethod) => {
            return (
              <HStack key={shippingMethod.type} pb={1} mb={1} alignItems={'center'} space={2}>
                <Image source={iconForShippingMethod(shippingMethod.type)} w={6} h={6} />
                <Text flex={1}>{nameForShippingMethod(shippingMethod.type)}</Text>
                <Text>{currency(shippingMethod?.cost ?? 0).format()}</Text>

                <Button
                  onPress={() => selectedUpdatedMethod(shippingMethod?.type ?? '')}
                  variant={'outline'}
                  size={'sm'}
                  isLoading={updating}
                >
                  Select
                </Button>
              </HStack>
            );
          })}
        </VStack>
      </Popover.Body>
    </Popover.Content>
  );
};
